<template>
  <div v-if="loading" class="mt-2">
    <v-skeleton-loader type="table-thead" max-height="45px"></v-skeleton-loader>
    <div v-if="plants.length" class="white rounded-lg">
      <v-skeleton-loader
        class="my-2 ma-4"
        v-for="plant in plants"
        :key="plant.id"
        type="table-row"
      ></v-skeleton-loader>
    </div>
    <div v-else-if="!component && !plants.length" class="white rounded-lg">
      <v-skeleton-loader
        class="my-2 ma-4"
        v-for="item in 5"
        :key="item"
        type="table-row"
      ></v-skeleton-loader>
    </div>
  </div>

  <div v-else class="PlantMonitor a-text">
    <div class="monitor-error">
      <Error v-if="errors.length" :errors="errors" />
    </div>
    <data-table :headers="tableHeaders" :items="items">
      <template #header>
        <thead>
          <tr :style="responsiveHeader">
            <th
              v-for="header in tableHeaders"
              :key="header.value"
              :style="{ height: isMobile ? '50px' : '40px' }"
            >
              {{ header.text }}
            </th>
            <th />
          </tr>
        </thead>
      </template>
      <template #tbody>
        <tbody :class="isMobile ? 'mt-2 ' : 'scrollbar'">
          <template v-if="!isMobile">
            <Row
              class="snapscroll"
              v-for="(item, i) in items"
              :key="i"
              :columns="headers"
              :item="item"
              @openAlarm="handleModal"
            />
          </template>
          <template v-else>
            <MobileRow
              class="snapscroll"
              v-for="(item, i) in items"
              :key="i"
              :columns="headers"
              :item="item"
              :hasStateText="!!mobileHeaders[0].text"
              @openAlarm="handleModal"
            />
          </template>
        </tbody>
      </template>
    </data-table>
    <v-dialog v-model="openAlarm" width="850px">
      <ModalAlarms @close="openAlarm = false" :reference="alarmReference" />
    </v-dialog>
  </div>
</template>

<script>
import DataTable from "@/components/DataTable/DataTableV2.vue";
import Row from "./Row.vue";
import MobileRow from "./MobileRow.vue";
import { RealtimeMeasure, Alarm, getComponent } from "@/models/lib.js";
import ModalAlarms from "@/components/Alarms/ModalAlarms";
import { mapGetters } from "vuex";

import Error from "@/components/Error/error.vue";
export default {
  components: {
    DataTable,
    Row,
    MobileRow,
    ModalAlarms,
    Error,
  },
  props: {
    plants: Array,
  },
  data: () => ({
    component: null,
    headers: [],
    mobileHeaders: [],
    headersMobile: [],
    items: [],
    options: {
      feature_name: "Portfolio",
      name: "Monitor",
    },
    plantAttributes: ["name"],
    // needed navigation button for each row
    buttonAttributes: ["id", "sourceType"],
    headerParser: {
      value: "value",
      text: "text",
    },
    row: {},
    loading: true,
    openAlarm: false,
    alarmReference: {},
    errors: [],
  }),
  watch: {
    plants: {
      handler() {
        if (this.plants.length) {
          this.loading = true;
          this.start();
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters(["showAlarms"]),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    tableHeaders() {
      return this.isMobile ? this.mobileHeaders : this.headers;
    },
    responsiveHeader() {
      let gridTemplateColumns = !this.isMobile
        ? `${this.showAlarms ? "0.1fr" : "0.33fr"} 0.25fr repeat(${
            this.tableHeaders.length - 2
          }, 1fr) 0.25fr`
        : this.showAlarms
        ? `0.2fr 0.3fr repeat(${this.tableHeaders.length - 2}, 1fr)`
        : `0.1fr repeat(${this.tableHeaders.length - 1}, 1fr) 0.3fr`;
      return {
        display: "grid",
        gridTemplateColumns,
        height: this.isMobile ? "45px" : "37px",
      };
    },
  },

  methods: {
    async start() {
      try {
        if (!this.component) {
          await this.fetchConfiguration();
          this.buildHeaders();
          this.buildMobileHeaders();
          this.addAlarms();
        }
        await this.fetchMeasurements();
        this.setTableItems();
      } catch (error) {
        this.handleErrors(error);
      }
      this.loading = false;
    },
    handleErrors(error) {
      this.headers.push({ text: "-", value: "notfound", key: "notfound" });
      this.items.push({ notfound: "-" });
      this.errors.push(error);
      console.error(error);
    },

    async fetchConfiguration() {
      const { feature_name, name } = this.options;
      this.component = await getComponent({ feature_name, name });
      if (!this.component) {
        throw `Component ${feature_name} - ${name} was not found`;
      }
      await this.component.setSpecific();
      this.component.connectMQTT();
    },
    async fetchMeasurements() {
      let plants = [...this.plants].map((e) => e.id);
      if (this.component) {
        this.component.unsubscribe();
        await this.component.setRealtime({ plants });
        await this.component.setAlarms({ plants });
        await this.component.setConstant({ plants });
        this.component.subscribe();
      }
    },
    buildHeaders() {
      const headers = this.component.getHeaders();
      headers.map((obj) => {
        obj.align = "left";
        return obj;
      });
      this.headers = [...headers];
      if (!this.headers.length) throw `Headers was not found`;
    },
    buildMobileHeaders() {
      const headers = this.component.getHeaders();
      const state = headers.find((e) => e.value.includes("state"));
      const size = state ? 3 : 2;

      const mobileHeaders = headers.splice(0, size);
      if (!mobileHeaders.length) {
        return;
      }
      const firstIndex = state ? 1 : 0;
      mobileHeaders[firstIndex].align = "center";

      // const lastIndex = mobileHeaders.length - 1;
      // mobileHeaders[lastIndex].align = "right";

      this.mobileHeaders = mobileHeaders;
      if (!this.mobileHeaders.length) throw `mobileHeaders was not found`;
    },
    addAlarms() {
      if (this.showAlarms) {
        this.headers.unshift({ align: "center", text: "", value: "alarm" });
        this.mobileHeaders.unshift({
          align: "center",
          text: "",
          value: "alarm",
        });
      }
    },
    getBaseRow() {
      const row = {};
      this.headers.forEach((h) => {
        const name = h.value;
        if (name === "state") {
          row[name] = new Alarm({ name });
        } else {
          row[name] = new RealtimeMeasure({ name });
        }
      });
      return row;
    },
    setTableItems() {
      const items = [];
      // each item correspond to one plant;
      this.plants.forEach((plant) => {
        const row = this.buildRow(plant);
        items.push(row);
      });

      this.items = items;
      if (!this.items.length) throw `Items was not found`;
    },
    buildRow(plant) {
      const base = this.getBaseRow();
      // const c = this.component;

      this.headers.forEach(({ value }) => {
        let item;

        if (value.includes(":")) {
          const values = value.split(":");
          item = this.findMultipleItems(values, plant);
          if (Array.isArray(item)) {
            item = item.length === 1 ? item[0] : item;
          }
        } else {
          item = this.findSingleItem(value, plant);
        }
        if (item) {
          base[value] = item;
        }
      });

      this.buttonAttributes.forEach((key) => {
        base[key] = plant[key];
      });

      return base;
    },
    findSingleItem(value, plant) {
      // const c = this.component;
      let item;
      if (value == "state") {
        item = this.findAlarm(value, plant.id);
      } else if (this.plantAttributes.includes(value)) {
        item = plant[value];
      } else {
        item = this.findRealtime(value, plant.id);
      }
      return item;
    },
    findMultipleItems(values = [], plant) {
      const items = [];
      values.forEach((value) => {
        let item;
        if (values[0] === "state") {
          item = this.findAlarm(value, plant.id);
        } else {
          item = this.findRealtime(value, plant.id);
        }
        if (item) {
          items.push(item);
        }
      });
      return items.length ? items : null;
    },
    findRealtime(value, plantId) {
      return this.component.findRealtime((r) => r.name == value && r.plant == plantId);
    },
    findAlarm(value, plantId) {
      return this.component.findAlarm((a) => a.name == value && a.plant == plantId);
    },
    handleModal(item) {
      this.openAlarm = true;
      this.alarmReference = item;
    },
  },
  beforeDestroy() {
    if (this.component) {
      this.component.unsubscribe();
    }
  },
};
</script>

<style lang="scss" scoped>
tr {
  text-align: center;
  width: 100%;
  border-radius: 4px;
}
.snapscroll {
  overflow-y: scroll;
  scroll-snap-type: y proximity;
  scroll-behavior: smooth;
  scroll-snap-align: start;
}
th:first-child {
  border-radius: 4px 0 0 4px !important;
  justify-content: start !important;
  padding-left: 10px;
}
th:last-child {
  border-radius: 0 4px 4px 0 !important;
  text-align: right;
}
.scrollbar {
  overflow-y: scroll;
  overflow-x: hidden;
  scroll-snap-type: y proximity;
  scroll-behavior: smooth;
  display: block;
  max-height: 51vh;
}
.scrollbar::-webkit-scrollbar-track {
  background-color: transparent !important;
}
.scrollbar::-webkit-scrollbar {
  width: 3px;
}
.scrollbar::-webkit-scrollbar-thumb {
  background-color: #bdbdbd81;
}
th {
  display: flex;
  align-items: center;
  justify-content: center;
  background: $secondary-light;
  margin-bottom: 1px;
}
.snapscroll {
  overflow-y: scroll;
  scroll-snap-type: y proximity;
  scroll-behavior: smooth;
  scroll-snap-align: start;
  overflow: hidden;
}
.monitor-error {
  position: absolute;
  right: 10px;
  top: 36vh;
}
</style>
